import { useContext, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { Alert, Flex, Grid } from '@zillow/constellation';
import { Box, Card } from '@zillow/constellation-10';
import { LoadingParagraph } from '@zg-rentals/loading-components';

import { VERTICAL_DISPLAY_TYPE } from '../../constants/PropertiesConstants';

import { DashboardContext } from '../../containers/DashboardPage/context';
import { RMWContext } from '../../providers/RMWContext';
import { LoadingMapContext } from '../../providers/LoadingMapContext/context';

import { useGetConversationCountsForListing, useInitiateCountsSync } from '../../hooks/queries/leads';
import { updateLoadingState } from '../../providers/LoadingMapContext/actions';

import { AppSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import { RMW_PATH } from '../../pathsManifest';

import EmptyInquiriesCard from './components/EmptyInquiriesCard';
import EmptyInquiriesCardSimple from './components/EmptyInquiriesCardSimple';
import InquiryCountCard from './components/InquiryCountCard';
import InquiriesContainer from './components/InquiriesContainer';
import SimpleCard from '../SimpleCard';

import type { State as DashboardState } from '../../containers/DashboardPage/reducer';

type Props = {
  displayType?: VERTICAL_DISPLAY_TYPE;
  updateMinModuleHeight?: () => void;
};

const CARD_LABELS = {
  NEW: 'New',
  NOT_REPLIED_TO: 'Not replied to',
  TOTAL: 'Total',
  SEE_MESSAGES: 'See messages',
  UNREPLIED: 'Unreplied',
};

const ConnectedInquiries = ({ displayType = VERTICAL_DISPLAY_TYPE.FULL, updateMinModuleHeight }: Props) => {
  const { dashboardState } = useContext(DashboardContext);
  const { paths } = useContext(RMWContext);
  const {
    reducerDispatch,
    loadingByComponent: { ConnectedInquiries: isConnectedInquiriesLoading },
  } = useContext(LoadingMapContext);

  const isScreenXlGTE = useSelector((state) => AppSelectors.makeIsScreenXlGTE(state));

  const {
    listingAlias,
    isEnabled: isScreeningCriteriaEnabled,
    dateLastActivatedTimestamp,
  } = useSelector((state) => {
    return {
      listingAlias: state.properties.propertyOverview.listingAlias,
      isEnabled: state.properties.propertyOverview.screeningCriteria.isEnabled,
      dateLastActivatedTimestamp: state.properties.currentProperty.dateLastActivatedTimestamp,
    };
  }, shallowEqual);

  const { data, isPending, error } = useGetConversationCountsForListing(listingAlias);

  const noConversations = data?.totalConversationsCount === 0;
  // __TEMPORARY__
  // will be replaced by https://jira.hotterpads.com/browse/ZRM-8454
  // accounts for mfh users who've never synced conversation counts
  useInitiateCountsSync(listingAlias, { enabled: Boolean(listingAlias && noConversations) });
  useEffect(() => {
    if (updateMinModuleHeight) {
      updateMinModuleHeight();
    }
  }, []);

  const { activePath }: Partial<DashboardState> = dashboardState;
  const isActivePathOverview = activePath
    ? activePath === RMW_PATH.DASHBOARD_OVERVIEW
    : displayType === VERTICAL_DISPLAY_TYPE.SIMPLE;

  const { unreadCount = 0, notRepliedToCount = 0, totalConversationsCount = 0 } = data || {};

  const gridRepeat = useMemo(() => {
    // layout when active path is overview
    if (isActivePathOverview) {
      return noConversations ? 1 : '3';
    }

    // layout for all other active paths (applications)
    return noConversations || isScreenXlGTE ? 1 : '3';
  }, [isActivePathOverview, noConversations, isScreenXlGTE]);

  useEffect(() => {
    updateLoadingState({
      reducerDispatch,
      data: {
        conversationCounts: isPending,
      },
    });
  }, [isPending, reducerDispatch]);

  const showSeeMessagesTile = !!data?.countsIncomplete;

  const displayChevron = isConnectedInquiriesLoading || (isScreenXlGTE ? true : false);
  const displayRowAlignLeft = !isConnectedInquiriesLoading && isActivePathOverview === false && isScreenXlGTE;

  const showScreeningCriteriaCTA = isScreeningCriteriaEnabled && dateLastActivatedTimestamp !== '';

  const getCardClickHandlerFn = (filter: string) => {
    return () => {
      // always filter with byProperty
      const queryParams = filter ? `?${filter}=true&byProperty=true` : '?byProperty=true';
      window.router.updateLocation(`${paths.base}/inbox/${listingAlias}${queryParams}`);
    };
  };

  if (showSeeMessagesTile) {
    return (
      <InquiriesContainer>
        <InquiryCountCard onClick={getCardClickHandlerFn('')} label={CARD_LABELS.SEE_MESSAGES} />
      </InquiriesContainer>
    );
  }

  const renderContent = () => {
    if (displayType === VERTICAL_DISPLAY_TYPE.SIMPLE) {
      return renderSimpleType();
    }

    return renderFullType();
  };

  const renderError = () => {
    if (displayType === VERTICAL_DISPLAY_TYPE.SIMPLE) {
      return (
        <Grid>
          <Alert appearance="error" body="There was an error retrieving your inquiries." />
        </Grid>
      );
    }

    return (
      <InquiriesContainer>
        <Card tone="neutral" css={{ height: '316px' }} elevated={false}>
          <Alert appearance="error" body="There was an error retrieving your inquiries." />
        </Card>
      </InquiriesContainer>
    );
  };

  /**
   * TODO: refactor to remove renderFullType
   * we can always use the simple type
   **/
  const renderFullType = () => {
    return (
      <InquiriesContainer>
        {totalConversationsCount === 0 && !isConnectedInquiriesLoading ? (
          <EmptyInquiriesCard showScreeningCriteriaCTA={showScreeningCriteriaCTA} />
        ) : (
          <Grid display="grid" gridTemplateColumns="repeat(2, 1fr)" gap="sm">
            <Grid
              gridColumnStart={isScreenXlGTE ? '0' : '1'}
              gridColumnEnd={isScreenXlGTE ? 'span 2' : 'span 1'}
              gridRow="0"
              gridRowEnd={isScreenXlGTE ? 'span 2' : 'span 1'}
            >
              <InquiryCountCard
                onClick={getCardClickHandlerFn('unreadOnly')}
                count={unreadCount}
                label={CARD_LABELS.NEW}
                isLoading={isConnectedInquiriesLoading}
              />
            </Grid>
            <Box css={{ gridColumnStart: '1', gridColumnEnd: 'span 2', display: { base: 'none', xl: 'block' } }}>
              <InquiryCountCard
                onClick={getCardClickHandlerFn('notRepliedToOnly')}
                count={notRepliedToCount}
                label={CARD_LABELS.NOT_REPLIED_TO}
                isLoading={isConnectedInquiriesLoading}
              />
            </Box>

            <Grid
              gridColumnStart={isScreenXlGTE ? '0' : '2'}
              gridColumnEnd={isScreenXlGTE ? 'span 2' : 'span 1'}
              gridRow={isScreenXlGTE ? '1' : '0'}
              gridRowEnd={isScreenXlGTE ? 'span 2' : 'span 1'}
            >
              <InquiryCountCard
                onClick={getCardClickHandlerFn('')}
                count={totalConversationsCount}
                label={CARD_LABELS.TOTAL}
                isLoading={isConnectedInquiriesLoading}
              />
            </Grid>
          </Grid>
        )}
      </InquiriesContainer>
    );
  };

  const renderSimpleType = () => {
    return (
      <Grid
        display="grid"
        gridTemplateColumns={{ default: `repeat(${isConnectedInquiriesLoading ? '3' : gridRepeat}, minmax(0,1fr))` }}
        gap={{ default: 'xs', lg: 'sm' }}
      >
        {totalConversationsCount === 0 && !isConnectedInquiriesLoading ? (
          <Grid>
            <EmptyInquiriesCardSimple showScreeningCriteriaCTA={showScreeningCriteriaCTA} />
          </Grid>
        ) : (
          <>
            <Grid data-testid="Grid-New">
              <SimpleCard cursor="pointer" withChevron={displayChevron} onClick={getCardClickHandlerFn('unreadOnly')}>
                <Flex
                  display="flex"
                  flexDirection={displayRowAlignLeft ? 'row-reverse' : 'column'}
                  justifyContent={displayRowAlignLeft ? 'flex-end' : 'center'}
                  alignItems="center"
                >
                  <LoadingParagraph textStyle="body-sm" isLoading={isConnectedInquiriesLoading} loadingWidth="50%">
                    {CARD_LABELS.NEW}
                  </LoadingParagraph>

                  {typeof unreadCount === 'number' && (
                    <LoadingParagraph
                      textStyle="body-sm-bold"
                      isLoading={isConnectedInquiriesLoading}
                      loadingWidth="90%"
                      css={{ marginTop: isConnectedInquiriesLoading ? '200' : '0' }}
                    >
                      {unreadCount}&nbsp;
                    </LoadingParagraph>
                  )}
                </Flex>
              </SimpleCard>
            </Grid>

            <Grid data-testid="Grid-Unreplied">
              <SimpleCard
                cursor="pointer"
                withChevron={displayChevron}
                onClick={getCardClickHandlerFn('notRepliedToOnly')}
              >
                <Flex
                  display="flex"
                  flexDirection={displayRowAlignLeft ? 'row-reverse' : 'column'}
                  justifyContent={displayRowAlignLeft ? 'flex-end' : 'center'}
                  alignItems="center"
                >
                  <LoadingParagraph textStyle="body-sm" isLoading={isConnectedInquiriesLoading} loadingWidth="50%">
                    {CARD_LABELS.UNREPLIED}
                  </LoadingParagraph>

                  {typeof notRepliedToCount === 'number' && (
                    <LoadingParagraph
                      textStyle="body-sm-bold"
                      isLoading={isConnectedInquiriesLoading}
                      loadingWidth="90%"
                      css={{ marginTop: isConnectedInquiriesLoading ? '200' : '0' }}
                    >
                      {notRepliedToCount}&nbsp;
                    </LoadingParagraph>
                  )}
                </Flex>
              </SimpleCard>
            </Grid>

            <Grid data-testid="Grid-Total">
              <SimpleCard cursor="pointer" withChevron={displayChevron} onClick={getCardClickHandlerFn('')}>
                <Flex
                  display="flex"
                  flexDirection={displayRowAlignLeft ? 'row-reverse' : 'column'}
                  justifyContent={displayRowAlignLeft ? 'flex-end' : 'center'}
                  alignItems="center"
                >
                  <LoadingParagraph textStyle="body-sm" isLoading={isConnectedInquiriesLoading} loadingWidth="50%">
                    {CARD_LABELS.TOTAL}
                  </LoadingParagraph>

                  {typeof totalConversationsCount === 'number' && (
                    <LoadingParagraph
                      textStyle="body-sm-bold"
                      isLoading={isConnectedInquiriesLoading}
                      loadingWidth="90%"
                      css={{ marginTop: isConnectedInquiriesLoading ? '200' : '0' }}
                    >
                      {totalConversationsCount}&nbsp;
                    </LoadingParagraph>
                  )}
                </Flex>
              </SimpleCard>
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  return <div data-testid="ConnectedInquiries">{error ? renderError() : renderContent()}</div>;
};

export { ConnectedInquiries };
export default ConnectedInquiries;
